import { createApi } from '@reduxjs/toolkit/query/react'
import { BACKEND_API_URL } from '../consts/api'
import { extractResponseData, extractResponseError, fetchBaseQueryWithToken } from '../utils/api'
import { getMediaSourceUrl } from '../utils/media'
import type {
  RecommendationCandidateUserMatch,
  RecommendationProjectMatch,
  RecommendationsListResponse,
  RecommendationsSummaryResponse,
  RecordRecommendationInteractionBody,
} from './types'

export const recommendationsTag = 'Recommendations' as const
export const recommendationsSummaryTag = 'RecommendationsSummary' as const

type RecommendationsEnvelope<T> = {
  data: RecommendationsListResponse<T>
  meta?: {
    next_cursor?: string | null
  }
}

function normalizeUser(user: RecommendationCandidateUserMatch['user']): RecommendationCandidateUserMatch['user'] {
  if (!user) return null

  return {
    ...user,
    avatar: getMediaSourceUrl(user.avatar),
  }
}

function normalizeProjectMatch(match: RecommendationProjectMatch): RecommendationProjectMatch {
  return match
}

function normalizeCandidateUserMatch(match: RecommendationCandidateUserMatch): RecommendationCandidateUserMatch {
  return {
    ...match,
    user: normalizeUser(match.user),
  }
}

function parseRecommendationsList<T>(response: unknown, normalizeItem: (item: T) => T): RecommendationsListResponse<T> {
  const root = response as RecommendationsEnvelope<T>
  const data = extractResponseData(response) as RecommendationsListResponse<T>

  return {
    exact: (data.exact ?? []).map(normalizeItem),
    potential: (data.potential ?? []).map(normalizeItem),
    quality: data.quality,
    missing_fields: data.missing_fields ?? [],
    meta: {
      next_cursor: root.meta?.next_cursor ?? null,
    },
  }
}

export const recommendationsApi = createApi({
  reducerPath: 'recommendationsApi',
  baseQuery: fetchBaseQueryWithToken(BACKEND_API_URL),
  tagTypes: [recommendationsTag, recommendationsSummaryTag],
  endpoints: (builder) => ({
    getRecommendationsSummary: builder.query<RecommendationsSummaryResponse, void>({
      query: () => ({
        url: '/users/me/recommendations/summary',
      }),
      transformResponse: (response: unknown) => extractResponseData(response) as RecommendationsSummaryResponse,
      transformErrorResponse: extractResponseError,
      providesTags: [recommendationsSummaryTag],
    }),

    getRecommendedProjectsForMe: builder.query<RecommendationsListResponse<RecommendationProjectMatch>, { limit?: number; cursor?: string | null } | void>({
      query: (arg) => ({
        url: '/users/me/recommendations/projects',
        params: {
          limit: arg?.limit,
          cursor: arg?.cursor,
        },
      }),
      transformResponse: (response: RecommendationsEnvelope<RecommendationProjectMatch>) =>
        parseRecommendationsList(response, normalizeProjectMatch),
      transformErrorResponse: extractResponseError,
      providesTags: [recommendationsTag],
    }),

    getRecommendedUsersForMyProjects: builder.query<
      RecommendationsListResponse<RecommendationCandidateUserMatch>,
      { limit?: number; projectId?: number; cursor?: string | null; kind?: 'role' | 'resource' } | void
    >({
      query: (arg) => ({
        url: '/users/me/recommendations/my-projects',
        params: {
          limit: arg?.limit,
          project_id: arg?.projectId,
          cursor: arg?.cursor,
          kind: arg?.kind,
        },
      }),
      transformResponse: (response: RecommendationsEnvelope<RecommendationCandidateUserMatch>) =>
        parseRecommendationsList(response, normalizeCandidateUserMatch),
      transformErrorResponse: extractResponseError,
      providesTags: [recommendationsTag],
    }),

    getProjectUserMatches: builder.query<
      RecommendationsListResponse<RecommendationCandidateUserMatch>,
      { projectId: number; limit?: number; cursor?: string | null; kind?: 'role' | 'resource' }
    >({
      query: ({ projectId, limit, cursor, kind }) => ({
        url: `/projects/${projectId}/matches/users`,
        params: {
          limit,
          cursor,
          kind,
        },
      }),
      transformResponse: (response: RecommendationsEnvelope<RecommendationCandidateUserMatch>) =>
        parseRecommendationsList(response, normalizeCandidateUserMatch),
      transformErrorResponse: extractResponseError,
      providesTags: [recommendationsTag],
    }),

    recordRecommendationInteraction: builder.mutation<unknown, RecordRecommendationInteractionBody>({
      query: (body) => ({
        url: '/recommendations/interactions',
        method: 'POST',
        body,
      }),
      transformResponse: extractResponseData,
      transformErrorResponse: extractResponseError,
      invalidatesTags: [recommendationsTag, recommendationsSummaryTag],
    }),
  }),
})

export const {
  useGetRecommendationsSummaryQuery,
  useGetRecommendedProjectsForMeQuery,
  useLazyGetRecommendedProjectsForMeQuery,
  useGetRecommendedUsersForMyProjectsQuery,
  useLazyGetRecommendedUsersForMyProjectsQuery,
  useGetProjectUserMatchesQuery,
  useLazyGetProjectUserMatchesQuery,
  useRecordRecommendationInteractionMutation,
} = recommendationsApi
